import React from 'react';
import { PatientForm } from '../../../../pages/PatientIdentification/Components/PatientForm';
import { useFormik } from 'formik';
import { INITIAL_VALUES } from '../../../../pages/PatientIdentification/utils/validateForm';
import { patientSchema } from '../../../../Schemas/patient';
import * as S from './styles';
import HeadTitle from '../../../../components/HeadTitle';
import { FieldsWrapper, LoadingOverlay } from '../../../../components';
import FooterWrapper from '../../../../components/FooterWrapper';
import Button from '../../../../components/Button';
import { PatientProps, usePatientContext } from '../../../../contexts/PatientContext';
import { maskCpf, maskDateStringIso, maskPassport, maskPhone, removeMask } from '../../../../utils/masks';
import { isOlderThan } from '../../../../utils/validators';
import { Page, useCheckUpRouter } from '../../router/context';
import { fetchCatalogService } from '../../../../modules/products';
import { verifyEligibility } from '../../../../modules/eligibility';
import { fetchValidateCheckup } from '../../../../modules/validateRules';
import { PatientScheduleBuilder, makePatientRuleByPatientProps } from '../../../../models/Patient';
import { PAYMENT_TYPES, PaymentValidationRule } from '../../../../models/PaymentRule';
import { EligibilityPlanProps } from '../../../../contexts/PaymentContext';
import TagInfo from '../../../../components/TagInfo';
import ModalNoEligibility from '../../../../components/ModalNoEligibility';
import SystemUnavailable from '../../../../components/SystemUnavailable';
import { UnavailableReturnsModal } from '../../components/UnavailableReturnsModal';
import { ReturnsAlredyScheduledModal } from '../../components/ReturnsAlredyScheduledModal';
import { LateAppointmentModal } from '../../components/LateAppointmentModal';
import { ServiceItemProps } from '../../../../models/Service';
import { LateAppointmentError } from '../../../../errors/LateAppointmentError';
import { UnavailableReturnsError } from '../../../../errors/UnavailableReturnsError';
import { FirstAppointmentUnavailableError } from '../../../../errors/FirstAppointmentUnavailableError';
import { SystemUnavailableError } from '../../../../errors';
import { useMainRouter } from '../../../../Router/pageComponents';
import { useServiceContext } from '../../../../contexts/ServiceContext';

const alertEinsteinIcon =
  'https://agendamento-images-nprd.telemedicinaeinstein.com.br/schedule-lib-assets/warningBlue.svg';

function formatPatientProps(patientData: any): PatientProps {
  const patient = {} as PatientProps;
  patient.name = patientData.name.trim();
  patient.email = patientData.email.trim();

  patient.document = removeMask(patientData.document);
  patient.phone = removeMask(patientData.phone);

  if (!isOlderThan(patientData.birthDate, 9)) patient.pregnant = false;
  else patient.pregnant = patientData.pregnant === 'Sim';

  return { ...patientData, ...patient };
}

export function PatientFormPage() {
  const [loading, setLoading] = React.useState(false);
  const [isModalNoEligibility, setIsModalNoEligibility] = React.useState(false);
  const [isModalLateAppointmentVisible, setIsModalLateAppointmentVisible] = React.useState(false);
  const [isModalUnavailableReturnsVisible, setIsModalUnavailableReturnsVisible] = React.useState(false);
  const [isModalSystemUnavailableVisible, setIsModalSystemUnavailableVisible] = React.useState(false);
  const [isModalReturnsAlredyScheduledVisible, setIsModalReturnsAlredyScheduledVisible] = React.useState(false);

  const { selectedService: service } = useServiceContext();

  const router = useCheckUpRouter();
  const { patientSelected } = usePatientContext();

  function makePaymentValidationByEligibilityPlan(eligibilityPlan: EligibilityPlanProps) {
    return {
      type: PAYMENT_TYPES.B2B,
      additional_info: {
        plan_code: eligibilityPlan.cod_plano
      }
    } as PaymentValidationRule;
  }

  async function validateElegibilityPlans({
    patient,
    serviceId
  }: {
    patient: PatientProps;
    serviceId: string;
  }): Promise<EligibilityPlanProps | undefined> {
    let eligibilities = [] as EligibilityPlanProps[];

    const { data, error, message } = await verifyEligibility({
      document: patient.document,
      documentType: patient.documentType,
      itemId: serviceId,
      eligibilityType: 'SERVICE',
      birthDate: patient.birthDate
    });

    if (error) {
      if (message === 'Plano não encontrado') setIsModalNoEligibility(true);
      if (data.length === 0 || message === 'Data de aniversário é inválida') setIsModalNoEligibility(true);
      else setIsModalSystemUnavailableVisible(true);
    }

    if (data.length === 0) setIsModalNoEligibility(true);
    else {
      eligibilities = data;
    }

    if (eligibilities.length === 1) {
      return eligibilities[0];
    }

    if (eligibilities.length > 1) {
      //  NAVEGAR PARA TELA DE SELEÇÃO DE PAGAMENTO
    }
  }

  function setPatient(patientSchedule: PatientProps) {
    const patient = PatientScheduleBuilder(patientSchedule.name);
    patient.setBirthDate(patientSchedule.birthDate);
    patient.setDocument(patientSchedule.document, patientSchedule.documentType);
    patient.setEmail(patientSchedule.email);
    patient.setPhone(patientSchedule.phone);
    patient.setGender(patientSchedule.gender);
    patient.setIsPregnant(patientSchedule.pregnant);
    return patient.build();
  }

  const handleError = (error: Error) => {
    if (error instanceof LateAppointmentError) setIsModalLateAppointmentVisible(true);
    if (error instanceof UnavailableReturnsError) setIsModalUnavailableReturnsVisible(true);
    if (error instanceof FirstAppointmentUnavailableError) setIsModalNoEligibility(true);
    if (error instanceof SystemUnavailableError) setIsModalSystemUnavailableVisible(true);
    // if (error instanceof FirstAppointmentUnderTimeLimit) setIsAppointmentUnderTimeLimit(true);
  };

  async function validateCheckup(props: {
    service: ServiceItemProps;
    patient: PatientProps;
    eligibilityPlan: EligibilityPlanProps;
  }) {
    try {
      const result = await fetchValidateCheckup({
        service: props.service,
        patient: makePatientRuleByPatientProps(props.patient),
        payment: makePaymentValidationByEligibilityPlan(props.eligibilityPlan)
      });

      const { isFirstAppointment, service, externalExams, firstAppointmentDate } = result;
      if (service?.products?.length === 0) setIsModalReturnsAlredyScheduledVisible(true);
      else
        router.navigate(Page.INFO, {
          patient: setPatient(props.patient),
          isFirstAppointment,
          service: {
            ...service,
            products: service.products.map(product => {
              return {
                ...product,
                name: product.name.replace('Check-Up Digital -', '').replace('Check Up Digital -', '').trim()
              };
            })
          },
          elegibility: props.eligibilityPlan,
          externalExams,
          firstAppointmentDate
        });
    } catch (error: any) {
      console.error('Error trying validate service', error);

      handleError(error);
    }
  }

  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema: patientSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async values => {
      setLoading(true);

      const patient = formatPatientProps(values);

      const res = await fetchCatalogService(service.id);

      const eligibilityPlan = await validateElegibilityPlans({ patient, serviceId: service.id });

      if (eligibilityPlan) await validateCheckup({ service: res.data, eligibilityPlan, patient });

      setLoading(false);
    }
  });

  function updateFormPatient(patient: PatientProps) {
    formik.setValues({
      ...patient,
      document: patient.documentType === 'CPF' ? maskCpf(patient.document) : maskPassport(patient.document),
      phone: maskPhone(patient.phone),
      birthDateDate: maskDateStringIso(patient.birthDate) || '',
      pregnant: patient.gender === 'Feminino' ? (patient.pregnant ? 'Sim' : 'Não') : '',
      gender: patient.gender === 'não informado' ? '' : patient.gender,
      idPatient: patient.idPatient || ''
    });
  }

  React.useEffect(() => {
    if (patientSelected) updateFormPatient(patientSelected);
  }, [patientSelected]);

  const { goBack: mainRouterGoBack } = useMainRouter();

  function handleGoBack() {
    if (router.history.length === 1) return mainRouterGoBack();
    else router.goBack();
  }

  return (
    <S.Container>
      <FieldsWrapper>
        <HeadTitle title="Informe os dados do paciente" goBack={handleGoBack} />
        <div className="info-wrapper">
          <TagInfo
            icon={<img src={alertEinsteinIcon} alt="alert icon" />}
            info="Os dados do paciente são confidenciais e de uso exclusivo dos serviços Einstein."
          />
        </div>
        <PatientForm formik={formik} validateGenderCatalogGenericRules={() => true} />
      </FieldsWrapper>
      <FooterWrapper>
        <S.ButtonWrapper>
          <Button title="Continuar" type="button" onClick={() => formik.submitForm()} />
        </S.ButtonWrapper>
      </FooterWrapper>
      <LoadingOverlay loading={loading} />
      <ModalNoEligibility
        visible={isModalNoEligibility}
        onClose={() => {
          setIsModalNoEligibility(false);
        }}
      />
      <SystemUnavailable
        visible={isModalSystemUnavailableVisible}
        onClose={() => setIsModalSystemUnavailableVisible(false)}
      />
      <UnavailableReturnsModal
        visible={isModalUnavailableReturnsVisible}
        onClose={() => setIsModalUnavailableReturnsVisible(false)}
      />
      <ReturnsAlredyScheduledModal
        visible={isModalReturnsAlredyScheduledVisible}
        onClose={() => setIsModalReturnsAlredyScheduledVisible(false)}
      />
      <LateAppointmentModal
        visible={isModalLateAppointmentVisible}
        onClose={() => setIsModalLateAppointmentVisible(false)}
      />
      <ModalNoEligibility visible={isModalNoEligibility} onClose={() => setIsModalNoEligibility(false)} />
      <LoadingOverlay loading={loading} />
    </S.Container>
  );
}
